<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import type { Ticket } from '../../lib/types';
  import TicketList from './TicketList.svelte';
  import TicketDetail from './TicketDetail.svelte';
  import CreateTicketModal from './CreateTicketModal.svelte';
  import FeatureRequests from '../features/FeatureRequests.svelte';

  // View state
  type ViewState = 'list' | 'detail';
  let viewState = $state<ViewState>('list');
  let selectedTicket = $state<Ticket | null>(null);
  let showCreateModal = $state(false);

  // Tab state
  type TabId = 'tickets' | 'features' | 'docs' | 'faq';
  let activeTab = $state<TabId>('tickets');

  // Documentation and FAQ data
  type DocLink = {
    title: string;
    description: string;
    url: string;
    icon: string;
  };

  type FaqItem = {
    question: string;
    answer: string;
  };

  const docLinks: DocLink[] = [
    {
      title: 'Getting Started',
      description: 'Learn the basics of EQUOS and set up your first business',
      url: '#',
      icon: 'rocket',
    },
    {
      title: 'Orders & Fulfillment',
      description: 'Create, manage, and fulfill orders efficiently',
      url: '#',
      icon: 'package',
    },
    {
      title: 'Inventory Management',
      description: 'Track stock levels, locations, and movements',
      url: '#',
      icon: 'inventory',
    },
    {
      title: 'Warehouse Operations',
      description: 'Inbound, outbound, and warehouse workflows',
      url: '#',
      icon: 'warehouse',
    },
    {
      title: 'User & Team Management',
      description: 'Manage staff, roles, and permissions',
      url: '#',
      icon: 'users',
    },
    {
      title: 'API Documentation',
      description: 'Integrate EQUOS with your systems',
      url: '#',
      icon: 'code',
    },
  ];

  const faqs: FaqItem[] = [
    {
      question: 'How do I add a new warehouse?',
      answer: 'Go to Businesses in the sidebar, select your business, then click "Add Warehouse" to create a new location.',
    },
    {
      question: 'Can I import inventory from a spreadsheet?',
      answer: 'Yes! In the EQUOS app, go to Inventory > Import and upload a CSV file with your product data.',
    },
    {
      question: 'How do I invite team members?',
      answer: 'Navigate to Team in the sidebar, click "Add Staff Member", and enter their email. They\'ll receive an invitation to join.',
    },
    {
      question: 'What happens if I exceed my order limit?',
      answer: 'You\'ll receive a notification when approaching your limit. You can upgrade your plan at any time from the Overview page.',
    },
  ];

  let expandedFaq = $state<number | null>(null);

  function toggleFaq(index: number) {
    expandedFaq = expandedFaq === index ? null : index;
  }

  function handleSelectTicket(ticket: Ticket) {
    selectedTicket = ticket;
    viewState = 'detail';
  }

  function handleBackToList() {
    viewState = 'list';
    selectedTicket = null;
  }

  function handleCreateSuccess() {
    // Refresh happens automatically via real-time listener
  }
</script>

<div class="support-container">
  <!-- Contact Support Section -->
  <section class="section hero-section">
    <div class="hero-content">
      <h2>How can we help?</h2>
      <p>Our support team is here to assist you with any questions or issues.</p>
      <div class="contact-options">
        <button class="contact-card" onclick={() => showCreateModal = true}>
          <div class="contact-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
            </svg>
          </div>
          <div class="contact-info">
            <h3>Create Ticket</h3>
            <p>Submit a support request</p>
          </div>
        </button>
        <a href="mailto:support@equosnine.com" class="contact-card">
          <div class="contact-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <div class="contact-info">
            <h3>Email Support</h3>
            <p>support@equosnine.com</p>
          </div>
        </a>
      </div>
    </div>
  </section>

  <!-- Tab Navigation -->
  <div class="tab-nav">
    <button
      class="tab-btn"
      class:active={activeTab === 'tickets'}
      onclick={() => { activeTab = 'tickets'; viewState = 'list'; selectedTicket = null; }}
    >
      My Tickets
    </button>
    <button
      class="tab-btn"
      class:active={activeTab === 'features'}
      onclick={() => activeTab = 'features'}
    >
      Feature Requests
    </button>
    <button
      class="tab-btn"
      class:active={activeTab === 'docs'}
      onclick={() => activeTab = 'docs'}
    >
      Documentation
    </button>
    <button
      class="tab-btn"
      class:active={activeTab === 'faq'}
      onclick={() => activeTab = 'faq'}
    >
      FAQ
    </button>
  </div>

  <!-- Tab Content -->
  {#if activeTab === 'tickets'}
    <section class="section tickets-section">
      {#if viewState === 'list'}
        <div class="tickets-header">
          <button class="create-ticket-btn" onclick={() => showCreateModal = true}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
            New Ticket
          </button>
        </div>
        <TicketList onSelectTicket={handleSelectTicket} />
      {:else if viewState === 'detail' && selectedTicket}
        <TicketDetail ticket={selectedTicket} onBack={handleBackToList} />
      {/if}
    </section>
  {:else if activeTab === 'features'}
    <section class="section features-section">
      <FeatureRequests />
    </section>
  {:else if activeTab === 'docs'}
    <!-- Documentation Section -->
    <section class="section">
      <div class="section-header">
        <h2>Documentation</h2>
        <p class="section-description">Learn how to get the most out of EQUOS.</p>
      </div>

      <div class="docs-grid">
        {#each docLinks as doc}
          <a href={doc.url} class="doc-card">
            <div class="doc-icon">
              {#if doc.icon === 'rocket'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                </svg>
              {:else if doc.icon === 'package'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                </svg>
              {:else if doc.icon === 'inventory'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
              {:else if doc.icon === 'warehouse'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                </svg>
              {:else if doc.icon === 'users'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              {:else if doc.icon === 'code'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              {/if}
            </div>
            <div class="doc-content">
              <h3>{doc.title}</h3>
              <p>{doc.description}</p>
            </div>
            <div class="doc-arrow">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
              </svg>
            </div>
          </a>
        {/each}
      </div>
      <p class="docs-note">Full documentation coming soon.</p>
    </section>
  {:else if activeTab === 'faq'}
    <!-- FAQ Section -->
    <section class="section">
      <div class="section-header">
        <h2>Frequently Asked Questions</h2>
      </div>

      <div class="faq-list">
        {#each faqs as faq, index}
          <div class="faq-item" class:expanded={expandedFaq === index}>
            <button class="faq-question" onclick={() => toggleFaq(index)}>
              <span>{faq.question}</span>
              <svg viewBox="0 0 20 20" fill="currentColor" class="faq-chevron">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
              </svg>
            </button>
            {#if expandedFaq === index}
              <div class="faq-answer">
                <p>{faq.answer}</p>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    </section>
  {/if}

  <!-- System Status -->
  <section class="section status-section">
    <div class="status-header">
      <div class="status-indicator online"></div>
      <span>All Systems Operational</span>
    </div>
    <a href="#" class="status-link">View Status Page</a>
  </section>
</div>

{#if showCreateModal}
  <CreateTicketModal
    onClose={() => showCreateModal = false}
    onSuccess={handleCreateSuccess}
  />
{/if}

<style>
  .support-container {
    max-width: 900px;
  }

  .section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .section-header {
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: #333;
  }

  .section-description {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }

  /* Hero Section */
  .hero-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .hero-content h2 {
    margin: 0 0 0.5rem;
    font-size: 1.75rem;
    color: white;
  }

  .hero-content > p {
    margin: 0 0 1.5rem;
    opacity: 0.9;
  }

  .contact-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }

  .contact-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    text-decoration: none;
    color: white;
    transition: background 0.2s;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
  }

  .contact-card:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  .contact-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    flex-shrink: 0;
  }

  .contact-icon svg {
    width: 24px;
    height: 24px;
  }

  .contact-info h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
  }

  .contact-info p {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.9;
  }

  /* Tab Navigation */
  .tab-nav {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    background: white;
    padding: 0.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .tab-btn {
    flex: 1;
    padding: 0.75rem 1rem;
    border: none;
    background: transparent;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
  }

  .tab-btn:hover {
    background: #f3f4f6;
    color: #333;
  }

  .tab-btn.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  /* Tickets Section */
  .tickets-section {
    padding: 0;
    overflow: hidden;
  }

  /* Features Section */
  .features-section {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  .tickets-header {
    display: flex;
    justify-content: flex-end;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .create-ticket-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .create-ticket-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .create-ticket-btn svg {
    width: 18px;
    height: 18px;
  }

  /* Documentation */
  .docs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  .doc-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    text-decoration: none;
    color: inherit;
    transition: all 0.2s;
  }

  .doc-card:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
  }

  .doc-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e0e7ff;
    border-radius: 8px;
    color: #667eea;
    flex-shrink: 0;
  }

  .doc-icon svg {
    width: 20px;
    height: 20px;
  }

  .doc-content {
    flex: 1;
    min-width: 0;
  }

  .doc-content h3 {
    margin: 0 0 0.25rem;
    font-size: 0.95rem;
    color: #333;
  }

  .doc-content p {
    margin: 0;
    font-size: 0.8rem;
    color: #666;
  }

  .doc-arrow {
    color: #999;
    flex-shrink: 0;
  }

  .doc-arrow svg {
    width: 20px;
    height: 20px;
  }

  .doc-card:hover .doc-arrow {
    color: #667eea;
  }

  .docs-note {
    margin: 1rem 0 0;
    font-size: 0.85rem;
    color: #999;
    font-style: italic;
    text-align: center;
  }

  /* FAQ */
  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .faq-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
  }

  .faq-question {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: #f9fafb;
    border: none;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 500;
    color: #333;
    text-align: left;
  }

  .faq-question:hover {
    background: #f3f4f6;
  }

  .faq-chevron {
    width: 20px;
    height: 20px;
    color: #666;
    transition: transform 0.2s;
    flex-shrink: 0;
  }

  .faq-item.expanded .faq-chevron {
    transform: rotate(180deg);
  }

  .faq-answer {
    padding: 1rem;
    background: white;
    border-top: 1px solid #e5e7eb;
  }

  .faq-answer p {
    margin: 0;
    font-size: 0.9rem;
    color: #555;
    line-height: 1.6;
  }

  /* Status Section */
  .status-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
  }

  .status-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .status-indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .status-indicator.online {
    background: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
  }

  .status-header span {
    font-weight: 500;
    color: #333;
  }

  .status-link {
    font-size: 0.9rem;
    color: #667eea;
    text-decoration: none;
  }

  .status-link:hover {
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    .tab-nav {
      flex-direction: column;
    }
  }
</style>
